/**
 * Client-side image resize for uploads, and the shared upload-error message
 * taxonomy that every upload surface maps its failures through.
 *
 * Every web upload surface routes its picked File through resizeForUpload()
 * before uploadBytes, so the output is always under the Storage rule limit for
 * that surface — the customer never sees a "file too large" rejection.
 *
 * Both image libraries are lazy-imported: browser-image-compression only when a
 * raster resize actually runs, heic2any (which embeds a large libheif build)
 * only when the input is detected as HEIC. Neither costs anything at first paint.
 */

import type { UploadFailureReason } from "../constants/imageAnalyticsEvents";

export type UploadSurface =
  "profile" | "coverArt" | "boxLogo" | "boxBanner" | "boxCoverArt" | "landingBanner";

export interface SurfaceTarget {
  /** The Storage rule's hard byte ceiling for this path, in MB. */
  ruleLimitMB: number;
  /** Client compression target — must stay under every ruleLimitMB. */
  maxSizeMB: number;
  /** Longest-edge clamp in px. A ceiling only: sources below it are never upscaled. */
  maxWidthOrHeight: number;
}

/**
 * Per-surface targets derived from the Storage rule limits (single source of
 * truth — if a rule limit changes, re-derive here). One conservative 4MB byte
 * target clears every rule limit, including the 5MB floor on profile/boxLogo.
 */
export const UPLOAD_SURFACE_TARGETS: Readonly<Record<UploadSurface, SurfaceTarget>> = {
  profile: { ruleLimitMB: 5, maxSizeMB: 4, maxWidthOrHeight: 1280 },
  coverArt: { ruleLimitMB: 10, maxSizeMB: 4, maxWidthOrHeight: 1280 },
  boxLogo: { ruleLimitMB: 5, maxSizeMB: 4, maxWidthOrHeight: 1280 },
  boxBanner: { ruleLimitMB: 10, maxSizeMB: 4, maxWidthOrHeight: 1920 },
  boxCoverArt: { ruleLimitMB: 10, maxSizeMB: 4, maxWidthOrHeight: 1280 },
  landingBanner: { ruleLimitMB: 10, maxSizeMB: 4, maxWidthOrHeight: 1920 },
};

export interface ResizeResult {
  file: File;
  bytesBefore: number;
  bytesAfter: number;
  durationMs: number;
  wasResized: boolean;
}

/**
 * The source image is too large to process safely on this device. Thrown
 * BEFORE any decode allocates the full bitmap, so a huge source produces this
 * catchable error instead of an uncatchable tab OOM or WASM abort.
 */
export class ImageTooLargeToProcess extends Error {
  constructor(message = "This photo is too large to process on this device.") {
    super(message);
    this.name = "ImageTooLargeToProcess";
  }
}

/**
 * The resize/transcode produced no usable image (library crash, corrupt or
 * zero-byte output, or output still over the rule limit). Callers surface an
 * honest "couldn't process this photo" — a broken blob is never uploaded.
 */
export class ImageProcessingFailed extends Error {
  constructor(message = "This photo couldn't be processed.") {
    super(message);
    this.name = "ImageProcessingFailed";
  }
}

/**
 * True when an upload failed because Storage security rules denied the write
 * (e.g. a box coach blocked from cover-art paths). Duck-typed on the Firebase
 * StorageError code so callers don't need the firebase/storage error class.
 */
function isPermissionDenied(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    (error as { code?: unknown }).code === "storage/unauthorized"
  );
}

/**
 * Picks the user-facing message for a failed image upload. The typed resize
 * errors are permanent for that image on that device and carry their own
 * plain-language copy; a Storage-rules denial gets the caller's not-allowed
 * copy when provided (a permission problem must never masquerade as a size or
 * network problem); anything else on the upload path is network-shaped, so
 * the caller's surface-specific fallback ("We couldn't upload your photo…")
 * applies. Keeping the taxonomy check here means adding a new typed error
 * updates every upload surface at once instead of requiring per-component
 * instanceof edits.
 */
export function messageForUploadError(
  error: unknown,
  fallback: string,
  options?: { notAllowed?: string }
): string {
  if (error instanceof ImageTooLargeToProcess || error instanceof ImageProcessingFailed) {
    return error.message;
  }
  if (options?.notAllowed && isPermissionDenied(error)) {
    return options.notAllowed;
  }
  return fallback;
}

/**
 * Classifies a failed upload for analytics: "size" (source too large to
 * process), "resize_error" (corrupt/failed transcode), "permission"
 * (Storage rules denied the write), else "network". Mirrors the same
 * taxonomy messageForUploadError shows users, so the metric buckets always
 * match the messages customers actually saw.
 */
export function uploadFailureReason(error: unknown): UploadFailureReason {
  if (error instanceof ImageTooLargeToProcess) return "size";
  if (error instanceof ImageProcessingFailed) return "resize_error";
  if (isPermissionDenied(error)) return "permission";
  return "network";
}

const MB = 1024 * 1024;
const MEGAPIXEL = 1_000_000;

/** Sources over this raw byte size are rejected before any read at all. */
const MAX_SOURCE_BYTES = 100 * MB;

/** How much of the file is read to sniff container headers for dimensions. */
const HEADER_SCAN_BYTES = 1 * MB;

/**
 * Decoding allocates ~4 bytes per pixel, so the pixel count — not the file
 * size — is what OOM-kills a memory-tight tab. The ceiling scales with
 * navigator.deviceMemory where reported (Chromium; capped self-reporting at
 * 8GB): low-memory devices get an honest error for photos that would crash
 * their tab, while capable devices accept every normal phone photo (a 48MP
 * full-res mode shot is ~192MB decoded).
 */
function maxSourcePixels(): number {
  const deviceMemoryGB = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  if (typeof deviceMemoryGB === "number") {
    if (deviceMemoryGB <= 2) return 16 * MEGAPIXEL;
    if (deviceMemoryGB <= 4) return 32 * MEGAPIXEL;
  }
  return 64 * MEGAPIXEL;
}

function isSvg(file: File): boolean {
  return file.type === "image/svg+xml" || /\.svg$/i.test(file.name);
}

const HEIC_BRANDS = ["heic", "heix", "hevc", "hevx", "heim", "heis", "mif1", "msf1"];

function fourcc(bytes: Uint8Array, offset: number): string {
  return String.fromCharCode(
    bytes[offset],
    bytes[offset + 1],
    bytes[offset + 2],
    bytes[offset + 3]
  );
}

/** HEIC detection: extension or MIME type, OR the ISO-BMFF ftyp brand. */
function isHeic(file: File, header: Uint8Array): boolean {
  if (/\.hei[cf]$/i.test(file.name)) return true;
  if (file.type === "image/heic" || file.type === "image/heif") return true;
  return (
    header.length >= 12 && fourcc(header, 4) === "ftyp" && HEIC_BRANDS.includes(fourcc(header, 8))
  );
}

interface Dimensions {
  width: number;
  height: number;
}

/**
 * Reads intrinsic dimensions from container headers WITHOUT decoding pixels.
 * Supports JPEG, PNG, GIF, WebP, and HEIC/HEIF. Returns null when the format
 * is unrecognized or the header is truncated — the pixel ceiling is then
 * best-effort skipped rather than blocking the upload.
 */
function readDimensionsFromHeader(bytes: Uint8Array): Dimensions | null {
  if (bytes.length < 24) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  // PNG: IHDR is always the first chunk after the 8-byte signature.
  if (view.getUint32(0) === 0x89504e47) {
    return { width: view.getUint32(16), height: view.getUint32(20) };
  }

  // GIF: logical screen descriptor, little-endian.
  if (fourcc(bytes, 0) === "GIF8") {
    return { width: view.getUint16(6, true), height: view.getUint16(8, true) };
  }

  // JPEG: walk marker segments to the first SOF (start-of-frame).
  if (view.getUint16(0) === 0xffd8) {
    let offset = 2;
    while (offset + 9 < bytes.length) {
      if (bytes[offset] !== 0xff) return null;
      // Skip fill bytes between segments.
      while (bytes[offset + 1] === 0xff && offset + 9 < bytes.length) offset++;
      const marker = bytes[offset + 1];
      const isSof = marker >= 0xc0 && marker <= 0xcf && ![0xc4, 0xc8, 0xcc].includes(marker);
      if (isSof) {
        return {
          height: view.getUint16(offset + 5),
          width: view.getUint16(offset + 7),
        };
      }
      offset += 2 + view.getUint16(offset + 2);
    }
    return null;
  }

  // WebP: RIFF container, three frame flavors.
  if (fourcc(bytes, 0) === "RIFF" && fourcc(bytes, 8) === "WEBP") {
    const chunk = fourcc(bytes, 12);
    if (chunk === "VP8X" && bytes.length >= 30) {
      const w = 1 + (bytes[24] | (bytes[25] << 8) | (bytes[26] << 16));
      const h = 1 + (bytes[27] | (bytes[28] << 8) | (bytes[29] << 16));
      return { width: w, height: h };
    }
    if (chunk === "VP8 " && bytes.length >= 30) {
      return {
        width: view.getUint16(26, true) & 0x3fff,
        height: view.getUint16(28, true) & 0x3fff,
      };
    }
    if (chunk === "VP8L" && bytes.length >= 25) {
      const b0 = bytes[21],
        b1 = bytes[22],
        b2 = bytes[23],
        b3 = bytes[24];
      return {
        width: 1 + (((b1 & 0x3f) << 8) | b0),
        height: 1 + (((b3 & 0x0f) << 10) | (b2 << 2) | ((b1 & 0xc0) >> 6)),
      };
    }
    return null;
  }

  // HEIC/HEIF: the ispe (image spatial extents) property holds intrinsic
  // dimensions inside the meta box near the file start. Walk the top-level
  // ISO-BMFF boxes (each header gives its size, so ~a dozen jumps) and scan
  // only the small meta box for ispe entries rather than the whole slice.
  // A HEIC can carry several ispe boxes (thumbnail + master); the largest is
  // what the decoder would materialize, so the largest is what the ceiling
  // must judge. A meta box beyond the scanned window means no dimensions —
  // best-effort null, same as any unparseable header.
  if (fourcc(bytes, 4) === "ftyp") {
    let boxStart = 0;
    while (boxStart + 8 <= bytes.length) {
      const boxSize = view.getUint32(boxStart);
      // Size 0 (extends to end of file) and 1 (64-bit largesize) are not
      // walkable with 32-bit offsets — stop rather than loop forever.
      if (boxSize < 8) return null;
      if (fourcc(bytes, boxStart + 4) === "meta") {
        let best: Dimensions | null = null;
        const boxEnd = Math.min(boxStart + boxSize, bytes.length);
        for (let i = boxStart; i + 16 <= boxEnd; i++) {
          if (
            bytes[i] === 0x69 &&
            bytes[i + 1] === 0x73 &&
            bytes[i + 2] === 0x70 &&
            bytes[i + 3] === 0x65 // "ispe"
          ) {
            const width = view.getUint32(i + 8);
            const height = view.getUint32(i + 12);
            if (width > 0 && height > 0 && (!best || width * height > best.width * best.height)) {
              best = { width, height };
            }
          }
        }
        return best;
      }
      boxStart += boxSize;
    }
    return null;
  }

  return null;
}

/**
 * Validates that a produced blob is a real, decodable image before it is
 * allowed anywhere near an upload. A corrupt or truncated transcode must
 * surface an honest error — never a "successful" upload of a broken image.
 */
async function assertDecodable(blob: Blob): Promise<void> {
  if (blob.size === 0) throw new ImageProcessingFailed();
  if (typeof createImageBitmap !== "function") {
    // Without ImageBitmap support the non-zero size check above is the only
    // guard; every supported evergreen browser has it.
    return;
  }
  let bitmap: ImageBitmap;
  try {
    // The output is already clamped to ≤1920px, so this decode is cheap.
    bitmap = await createImageBitmap(blob);
  } catch {
    throw new ImageProcessingFailed();
  }
  const ok = bitmap.width > 0 && bitmap.height > 0;
  bitmap.close?.();
  if (!ok) throw new ImageProcessingFailed();
}

function replaceExtension(name: string, ext: string): string {
  const base = name.replace(/\.[^.]+$/, "");
  return `${base}.${ext}`;
}

const now = (): number => (typeof performance !== "undefined" ? performance.now() : Date.now());

/**
 * Same-origin URL of the vendored browser-image-compression script that the
 * library's web worker imports at compression time. Without this option the
 * worker importScripts the library from the jsdelivr CDN on every upload —
 * a third party learning the customer's IP at upload time, an un-timeouted
 * fetch that can hang the upload on flaky gym wifi, and a break-waiting-to-
 * happen under any future Content-Security-Policy. The vendored copies live
 * in each app's public/vendor/ and are drift-guarded against the installed
 * dependency by vendoredCompressionLib.test.ts.
 *
 * Must be ABSOLUTE: the worker runs on a blob: URL, where a root-relative
 * importScripts path has no base to resolve against.
 */
function selfHostedCompressionLibUrl(): string {
  return `${window.location.origin}/vendor/browser-image-compression.js`;
}

/**
 * Resizes/compresses a picked image so it is guaranteed to land under the
 * Storage rule limit for the given surface. SVGs pass through untouched
 * (not raster-resizable). HEIC inputs are transcoded to JPEG first.
 *
 * @throws ImageTooLargeToProcess when the source exceeds the device-aware
 *   pixel ceiling (checked before any decode) or an absurd byte size.
 * @throws ImageProcessingFailed when the transcode/compression fails or
 *   produces an invalid or over-limit output.
 */
export async function resizeForUpload(file: File, surface: UploadSurface): Promise<ResizeResult> {
  const start = now();
  const bytesBefore = file.size;
  const target = UPLOAD_SURFACE_TARGETS[surface];

  if (isSvg(file)) {
    return {
      file,
      bytesBefore,
      bytesAfter: bytesBefore,
      durationMs: now() - start,
      wasResized: false,
    };
  }

  if (file.size > MAX_SOURCE_BYTES) throw new ImageTooLargeToProcess();

  const header = new Uint8Array(await file.slice(0, HEADER_SCAN_BYTES).arrayBuffer());

  const dimensions = readDimensionsFromHeader(header);
  if (dimensions && dimensions.width * dimensions.height > maxSourcePixels()) {
    throw new ImageTooLargeToProcess();
  }

  let working: File = file;
  if (isHeic(file, header)) {
    try {
      const { default: heic2any } = await import("heic2any");
      const converted = await heic2any({ blob: file, toType: "image/jpeg" });
      const jpegBlob = Array.isArray(converted) ? converted[0] : converted;
      working = new File([jpegBlob], replaceExtension(file.name, "jpg"), {
        type: "image/jpeg",
      });
    } catch (error) {
      if (error instanceof ImageTooLargeToProcess) throw error;
      throw new ImageProcessingFailed();
    }
  }

  let compressed: Blob;
  try {
    const { default: imageCompression } = await import("browser-image-compression");
    compressed = await imageCompression(working, {
      maxSizeMB: target.maxSizeMB,
      maxWidthOrHeight: target.maxWidthOrHeight,
      useWebWorker: true,
      libURL: selfHostedCompressionLibUrl(),
      // The re-encode must drop all EXIF, most importantly GPS — a geotagged
      // photo must never reach Storage with its location attached (GDPR
      // Art. 5(1)(c) data minimisation).
      preserveExif: false,
    });
  } catch {
    throw new ImageProcessingFailed();
  }

  // The compression library iterates quality toward maxSizeMB; if the output
  // still breaches the rule limit the upload WOULD be rejected — fail honestly
  // here instead.
  if (compressed.size >= target.ruleLimitMB * MB) throw new ImageProcessingFailed();

  await assertDecodable(compressed);

  const outputFile = new File([compressed], working.name, {
    type: compressed.type || working.type,
  });

  return {
    file: outputFile,
    bytesBefore,
    bytesAfter: outputFile.size,
    durationMs: now() - start,
    wasResized: true,
  };
}
